<template>
  <ModalRoot class="weather-modal" @close="$emit('close')">
    <div class="weather-modal__container">
      <div class="weather-modal__header"> 
        <div class="weather-modal__title">średnia temperatura w sezonie</div>
        <div class="weather-modal__location" v-html="location" />
      </div>
      <div class="weather-modal__tables">
        <table class="weather-table weather-table--side weather-table--side-upper">
          <tr>
            <td>Miesiąc</td>
          </tr>
          <tr>
            <td>
              <span class="mobileTextVersion">Temp.<br>w dzień °C</span>
              <span class="tabletAndDesktopTextVersion">Temperatura w dzień °C</span>
              <img src="/assets/images/icons/weather-widget/sun-and-cloud.svg">
            </td>
          </tr>
          <tr>
            <td>
              <span class="mobileTextVersion">Temp.<br>w nocy °C</span>
              <span class="tabletAndDesktopTextVersion">Temperatura w nocy °C</span>
              <img src="/assets/images/icons/weather-widget/night.svg">
            </td>
          </tr>
        </table>
        <table class="weather-table weather-table--side weather-table--side-bottom">
          <tr>
            <td>
              <span class="mobileTextVersion">Temp.<br>wody °C</span>
              <span class="tabletAndDesktopTextVersion">Temperatura wody °C</span>
              <img src="/assets/images/icons/weather-widget/waves.svg">
            </td>
          </tr>
          <tr>
            <td>
              <span class="mobileTextVersion">Średnia liczba godzin słonecznych</span>
              <span class="tabletAndDesktopTextVersion">Średnia<br>liczba godzin słonecznych<br>w ciągu doby</span>
              <img src="/assets/images/icons/weather-widget/sun.svg">
            </td>
          </tr>
        </table>
        <div class="weather-table__overflow">
          <table class="weather-table weather-table--upper">
            <tr>
              <td v-for="(month, index) in monthsAsRomanNumber" :key="index">{{ month }}</td>
            </tr>
            <tr>
              <td v-for="(temperature, index) in dayTemperatures" :key="index">{{ temperature }}<span class="deg">&deg;</span></td>
            </tr>
            <tr>
              <td v-for="(temperature, index) in nightTemperatures" :key="index">{{ temperature }}<span class="deg">&deg;</span></td>
            </tr>
          </table>
          <table class="weather-table weather-table--bottom">
            <tr>
              <td v-for="(temperature, index) in waterTemperatures" :key="index">{{ temperature }}<span class="deg">&deg;</span></td>
            </tr>
            <tr>
              <td v-for="(hours, index) in sunnyHours" :key="index">
                <img :src="'/assets/images/icons/weather-widget/sun.svg'">
                <div class="weather-table__hours"><span class="weather-table__hours--sunny">{{ hours }}</span>h</div>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  </ModalRoot>
</template>

<script>
import Vue from 'vue'
import Component from 'vue-class-component'

import ModalRoot from '@/components/ModalRoot'
import { getRomanNumeral } from '@/utils/getRomanNumeral'

@Component({
  components: {
    ModalRoot,
  },
  props: {
    data: {
      type: Object,
      required: false,
    },
  },
})
export default class WeatherModal extends Vue {
  weatherConfig = { min: this.data.seasonFirstMonth, max: this.data.seasonLastMonth }

  get yearlyData () {
    return {
      ...this.data.YearlyData,
      Months: this.data.YearlyData.Months
        .map((month, i) => ({ ...month, id: i + 1, asRoman: getRomanNumeral(i + 1) }))
        .filter(el => el.id >= this.weatherConfig.min && el.id <= this.weatherConfig.max),
    }
  }

  get location () {
    return this.yearlyData.LocationName
  }

  get dayTemperatures () {
    return this.yearlyData.Months.map(month => Math.round(month.TempDay))
  }

  get nightTemperatures () {
    return this.yearlyData.Months.map(month => Math.round(month.TempNight))
  }

  get waterTemperatures () {
    return this.yearlyData.Months.map(month => Math.round(month.TempWater))
  }

  get sunnyHours () {
    return this.yearlyData.Months.map(month => Math.round(month.Sun))
  }

  get monthsAsRomanNumber () {
    return this.yearlyData.Months.map(month => month.asRoman)
  }
}
</script>

<style lang="scss" scoped>
$weather-table-border: 1px solid #EDEDED;
$tables-gap: 21px;
$basic-cell-height--tablet: 51px;
$basic-cell-height--desktop: 48px;

.weather-table {
  background: #FFF;
  font-weight: 300;

  td {
    @include media(desktop) {
      line-height: 1.2;
      padding-top: 5px;
    }
  }

  &--side {
    box-shadow: 0px 0px 15px rgba(0, 0, 0, .25);
    z-index: 1;
    font-size: 0.85em;
    width: 100%;

    @include media(tablet) {
      position: static;
      box-shadow: none;
      font-size: 1em;
    }

    tr:first-child td {
      border-top: none;
    }

    td {
      padding: 2px 10px;
      font-family: Georgia;
      font-style: italic;
      text-align: right;

      @include media(tablet) {
        border-right: $weather-table-border;
        border-top: $weather-table-border;
      }
    }
  }

  &--side-upper {
    grid-column: 1 / 2;
    grid-row: 1 / 2;

    tr:first-child td {
      height: 34px;

      @include media(tablet) {
        height: 36px;
      }
    }

    td {
      height: 51px;

      @include media(tablet) {
        height: $basic-cell-height--tablet;
        display: grid;
        grid-template-columns: 1fr 32px;
        grid-column-gap: 10px;
        justify-content: right;
        align-items: center;
      }

      @include media(desktop) {
        height: $basic-cell-height--desktop;
      }
    }

    img {
      display: none;

      @include media(tablet) {
        display: block;
        width: 32px;
        height: 32px;
      }
    }
  }

  &--side-bottom {
    grid-column: 1 / 2;
    grid-row: 2 / 3;

    tr:first-child td {
      height: 50px;

      @include media(desktop) {
        height: $basic-cell-height--desktop;
      }
    }

    tr:last-child td {
      padding-left: 2px;

      @include media(tablet) {
        align-items: flex-start;
        padding: 20px 10px 25px 2px;
      }
    }

    td {
      height: 85px;

      @include media(tablet) {
        display: grid;
        grid-template-columns: 1fr 32px;
        grid-column-gap: 10px;
        justify-content: right;
        align-items: center;
        height: 112px;        
      }

      @include media(desktop) {
      }
    }

    img {
      display: none;

      @include media(tablet) {
        display: block;
        width: 32px;
        height: 32px;
      }
    }
  }

  &__overflow {
    grid-column: 2 / 3;
    grid-row: 1 / 3;
    overflow: auto;
    text-align: center;
    font-family: 'Gotham Book';

    @include media(tablet) {
      overflow: initial;
    }
  }

  &--upper {
    table-layout: fixed;
    width: 655px;
    margin-bottom: $tables-gap;

    @include media(tablet) {
      width: 100%;
    }

    tr:first-child td {
      font-size: 14px;
      height: 36px;
      background: #005CA9;
      color: white;
      font-weight: bold;
      font-family: 'Gotham';
    }

    td {
      height: 50px;
      font-size: 20px;
      padding-top: 2px;

      @include media(tablet) {
        height: $basic-cell-height--tablet;
      }

      @include media(desktop) {
        height: $basic-cell-height--desktop;
      }
    }

    td + td, th + th {
      border-left: $weather-table-border;
    }

    tr + tr {
      border-top: $weather-table-border;
    }
  }

  &--bottom {
    table-layout: fixed;
    width: 655px;

    @include media(tablet) {
      width: 100%;
    }

    td {
      padding-top: 2px;
    }

    tr:first-child td {
      font-size: 20px;
      height: 50px;

      @include media(desktop) {
        height: $basic-cell-height--desktop;
      }
    }

    tr:nth-child(2) td {
      height: 85px;
      padding-bottom: 10px;
      padding-top: 7px;

      @include media(tablet) {
        height: 112px;
      }

      @include media(desktop) {
        padding-top: 23px;
      }
    }

    td + td, th + th {
      border-left: $weather-table-border;
    }

    tr + tr {
      border-top: $weather-table-border;
    }

    img {
      width: 40px;

      @include media(desktop) {
        width: 37px;
      }
    }
  }

  &__hours {
    margin-top: 4px;
    font-size: 18px;

    @include media(tablet) {
      font-size: 16px;
    }

    @include media(desktop) {
      margin-top: 6px;
    }

    &--sunny {
      font-weight: bold;
    }
  }

  .deg {
    font-size: 23px;
  }
}

.mobileTextVersion {
  display: initial;

  @include media(tablet) {
    display: none;
  }
}

.tabletAndDesktopTextVersion {
  display: none;

  @include media(tablet) {
    display: initial;
  }
}
</style>

<style lang="scss">
.weather-modal {
  .modal-dialog__wrapper {
    padding: 0;
    height: 100%;
    margin-top: 25px;

    @include media(tablet) {
      padding: 0 20px;
      height: unset;
      margin-top: 33px;
    }

    @include media(desktop) {
      margin-top: 57px;
    }
  }

  .modal-dialog--close {
    padding-right: 21px;
    padding-bottom: 3px;
    margin-top: -5px;

    @include media(tablet) {
      padding: unset;
      margin-top: unset;
    }
  }
}

.weather-modal {
  color: #005CA9;

  &__container {
    background-color: #EDEDED;
    background-size: cover;
    padding-top: 35px;
    height: 100%;
    line-height: 1.3;
    -webkit-overflow-scrolling: touch;

    @include media(tablet) {
      background-image: url('/assets/images/weather-modal.jpg');
      padding: 56px 20px 90px 20px;
      height: unset;
    }

    @include media(desktop) {
      padding: 87px 76px 136px 76px;
    }
  }

  &__header {
    margin-bottom: 36px;
    padding: 0 10px 0 10px;

    @include media(tablet) {
      margin-bottom: 25px;
      padding: 0;
    }

    @include media(desktop) {
      margin-bottom: 16px;
    }
  }

  &__title {
    font-family: Gotham;
    font-size: 22px;
    font-weight: bold;
    text-transform: uppercase;
    margin-left: 6px;
    line-height: 1.2em;

    @include media(tablet) {
      margin-left: 0;
    }
  }

  &__location {
    font-family: Georgia;
    font-size: 18px;
    font-style: italic;
    font-weight: lighter;
    margin-left: 8px;

    @include media(tablet) {
      margin-left: 0;
      margin-top: 2px;
    }

    &::before {
      content: "";
      display: inline-block;
      height: 6px;
      width: 40px;
      background: #005CA9;
      margin-right: 20px;

      @include media(tablet) {
        margin-right: 16px;
      }
    }
  }

  &__tables {
    display: grid;
    grid-template-columns: 88px auto;
    grid-row-gap: 21px;

    @include media(tablet) {
      grid-template-columns: 150px auto;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/weather-widget/WeatherModal.vue